/**
 * Voyado Elevate: session linking, payment preparation, add-to-cart and
 * favorites.
 *
 * Five jobs:
 *
 * 1. Sync the logged-in member's Elevate customerKey with their Engage
 *    contact's discoveryKey, once per login. See syncDiscoveryKeyWithEngage
 *    below - this is NOT "set customerKey to customer.id". Voyado's own
 *    identification flowcharts (developer.voyado.com/docs/elevate/
 *    engage-with-elevate) keep customerKey as whatever value the browser
 *    already established (an anonymous UUID Elevate generates itself on
 *    first visit, persisted client-side) all the way through identification
 *    - overwriting it with customer.id on login would sever that value from
 *    everything the visitor did anonymously before logging in.
 *
 * 2. Put the Elevate session keys on the cart object so the backend can send
 *    the payment notification. That notification has to go server-side because
 *    it requires the API key, which must not live in the theme. Skipped
 *    entirely without analytics consent (Cookiebot "Statistics"), since the
 *    session-key gate in elevate-core.js hands back a throwaway random pair
 *    in that case, worthless as an order-attribution key.
 *

 * 3. Send the add-to-cart notification, required per Voyado's own
 *    notification checklist and previously missing entirely - see
 *    notifyAddToCartOnSubmit() below.
 *
 * 4. Identify a visitor arriving from a CG newsletter link (Engage's own
 *    "soft identification") - see identifyFromEclubLink below. A separate
 *    trigger from job 1: fires from a URL param, not a login, and doesn't
 *    require a Shopify account at all.
 *
 * 5. Send add/remove-favorite notifications when the visitor uses the
 *    wishlist heart, so Elevate's FAVORITES algorithm has a data source at
 *    all - see the favorites bridge at the bottom of this file.
 *
 * Note: Voyado's example code listens for a `cart:change` event. That event does
 * not exist in this theme. We use the theme's own pubsub,
 * PUB_SUB_EVENTS.cartUpdate from assets/constants.js. Job 2 handles both
 * sources ('product-form' and 'cart-items'), since assets/cart.js:78-85
 * filters out 'cart-items' and copying that logic would have missed quantity
 * changes made in the cart drawer. Job 3 deliberately does not: 'cart-items'
 * is a quantity change on something already in the cart, not a new add, and
 * notifying it as one would double-count.
 *
 * See docs/voyado-elevate-implementering.md sections 5 and 7.
 */

const ENGAGE_DISCOVERY_KEY_SYNCED_FOR_KEY = 'cg_engage_discovery_key_synced_for';

let cartAttrsStamped = false;

// Readiness lives in assets/elevate-core.js, which loads before this file.
// Deliberately one implementation, not one per surface.
const whenElevateReady = () => window.CGElevate.whenReady();

/**
 * `window.CGCustomerEmail` is set in layout/theme.liquid, but only inside
 * {% if customer.id != blank %}, so it is undefined for logged-out visitors.
 *
 * Deliberately not a bare global `customerEmail`: Wishlist Guru declares a
 * top-level `const customerEmail` of its own, and a clashing global
 * declaration stops the app's script for every logged-in customer.
 */
function getShopifyCustomerEmail() {
  const email = window.CGCustomerEmail;
  return email ? String(email) : null;
}

/**
 * Syncs this browser's current Elevate customerKey with Engage's
 * discoveryKey for the logged-in member, once per (email, customerKey)
 * pair. Talks to cg-elevate-payment-worker's /discovery-key, never to
 * Engage directly - that needs an API key that must not live in the theme.
 *
 * Three outcomes, per Voyado's own identification flow:
 * - Contact has no discoveryKey yet: the worker pushes this browser's
 *   current customerKey to Engage - it becomes the canonical identity.
 * - Contact already has a *different* discoveryKey (e.g. set earlier via a
 *   newsletter link): adopt it instead, via updateCustomerKey - reuses the
 *   identity Engage already knows this person by, rather than fragmenting
 *   their history under a second, unrelated key.
 * - Not a member (no matching Engage contact): does nothing. Never creates
 *   a contact - see cg-elevate-payment-worker's src/discovery-key.ts.
 *
 * A "not a member" outcome is deliberately never cached in localStorage,
 * unlike the other two - so a visitor who becomes a member later is
 * retried on their next login instead of being silently skipped forever.
 * The Engage lookup itself is what decides membership, not a Shopify-side
 * flag - a metafield here could be stale.
 *
 * Gated on analytics consent, same as job 2 below - this sends
 * identifying data (email, customerKey) to a third party. Cookiebot has no
 * separate "marketing" category wired up in this theme yet; revisit if
 * that changes.
 *
 * Returns the customerKey now in effect (possibly the adopted one), or
 * null if nothing could be determined (logged out, sync disabled, no
 * consent, not a member, etc). Callers needing "the current customerKey
 * regardless" should read elevate.query.c.session() themselves.
 */
async function syncDiscoveryKeyWithEngage(elevate) {
  const email = getShopifyCustomerEmail();
  if (!email) {
    // Logged out. Clear the flag so the next login syncs again.
    localStorage.removeItem(ENGAGE_DISCOVERY_KEY_SYNCED_FOR_KEY);
    return null;
  }

  // hasAnalyticsConsent() reads window.Shopify.customerPrivacy synchronously,
  // which is not guaranteed to exist yet this early in the page lifecycle -
  // this call fires from the same whenElevateReady() tick that first kicks
  // off loading it. Without awaiting it here, a real "yes" reads as "no"
  // on a fresh page load, confirmed empirically 2026-09-23 (hasAnalyticsConsent()
  // correctly returned true moments later, from the console, on the same page).
  await window.CGElevate.loadCustomerPrivacy();
  if (!window.CGElevate.hasAnalyticsConsent()) return null;

  const workerUrl = window.CGElevateEngageWorkerUrl;
  if (!workerUrl) return null;

  const session = (await elevate.query.c.session()) || {};
  const currentCustomerKey = session.customerKey;
  if (!currentCustomerKey) return null;

  const syncedFor = email + ':' + currentCustomerKey;
  if (localStorage.getItem(ENGAGE_DISCOVERY_KEY_SYNCED_FOR_KEY) === syncedFor) {
    return currentCustomerKey;
  }

  let result;
  try {
    const response = await fetch(workerUrl.replace(/\/$/, '') + '/discovery-key', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerKey: currentCustomerKey, email: email })
    });
    if (!response.ok) return null;
    result = await response.json();
  } catch (e) {
    return null;
  }

  let effectiveCustomerKey = currentCustomerKey;
  if (
    result.status === 'adopt' &&
    result.discoveryKey &&
    result.discoveryKey !== currentCustomerKey &&
    elevate.session &&
    typeof elevate.session.updateCustomerKey === 'function'
  ) {
    elevate.session.updateCustomerKey(result.discoveryKey);
    effectiveCustomerKey = result.discoveryKey;
  }

  if (result.status === 'updated' || result.status === 'adopt') {
    localStorage.setItem(ENGAGE_DISCOVERY_KEY_SYNCED_FOR_KEY, email + ':' + effectiveCustomerKey);
  }

  return effectiveCustomerKey;
}

const ENGAGE_ECLUB_PARAM = 'eClub';

/**
 * Newsletter soft identification. CG sends newsletters through Engage;
 * clicking through carries an encrypted `eClub` query param that decrypts
 * to this contact's real discoveryKey - see
 * https://developer.voyado.com/docs/contacts/identify-contact/soft-identification.md.
 *
 * Decryption happens in cg-elevate-payment-worker's /decrypt-eclub, not
 * here - a different reason than "API key must not live in the theme":
 * AES-CBC has no built-in authentication, so whoever holds the shared key
 * can also *encrypt*. A theme-side key would let anyone forge a link that
 * decrypts to an arbitrary discoveryKey, and updateCustomerKey() below is
 * a purely local SDK write - no Voyado call needed to make that stick.
 * See that endpoint's own header comment.
 *
 * Independent of Shopify login - this identifies by Engage contact alone,
 * so it runs for a visitor with no Shopify account too. Gated on analytics
 * consent, same reasoning as syncDiscoveryKeyWithEngage above.
 *
 * Strips the eClub param from the visible URL as soon as it's read,
 * before the consent/worker-URL checks below - so the encrypted value
 * never lingers in the address bar, browser history, or gets bookmarked/
 * shared onward, even on a visit with no consent yet. Trade-off: a
 * no-consent-yet visit loses this identification for good rather than
 * retrying if consent is granted later in the same page view - accepted
 * for now, not worth the extra complexity for how rarely that timing
 * would actually occur.
 */
async function identifyFromEclubLink(elevate) {
  const url = new URL(window.location.href);
  const eclub = url.searchParams.get(ENGAGE_ECLUB_PARAM);
  if (!eclub) return null;

  url.searchParams.delete(ENGAGE_ECLUB_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());

  // See syncDiscoveryKeyWithEngage's identical await above for why this is
  // needed - hasAnalyticsConsent() alone races the Customer Privacy API's
  // own async load on a fresh page.
  await window.CGElevate.loadCustomerPrivacy();
  if (!window.CGElevate.hasAnalyticsConsent()) return null;

  const workerUrl = window.CGElevateEngageWorkerUrl;
  if (!workerUrl) return null;

  let result;
  try {
    const response = await fetch(workerUrl.replace(/\/$/, '') + '/decrypt-eclub', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eclub: eclub })
    });
    if (!response.ok) return null;
    result = await response.json();
  } catch (e) {
    return null;
  }

  if (!result.discoveryKey || !elevate.session || typeof elevate.session.updateCustomerKey !== 'function') {
    return null;
  }

  elevate.session.updateCustomerKey(result.discoveryKey);
  if (result.email) {
    localStorage.setItem(ENGAGE_DISCOVERY_KEY_SYNCED_FOR_KEY, result.email + ':' + result.discoveryKey);
  }
  return result.discoveryKey;
}

/**
 * Writes the Elevate session keys to cart.attributes when they are missing or
 * stale. The attributes have to be on the cart before the customer reaches
 * checkout, otherwise the backend has nothing to send the payment notification
 * with.
 *
 * requireExisting prevents us from creating empty carts. Without it, every page
 * view from a casual visitor would write a cart to Shopify.
 *
 * customerKeyOverride is used right after login/eClub identification, see
 * syncDiscoveryKeyWithEngage/identifyFromEclubLink.
 */
async function writeElevateCartAttributes(customerKeyOverride, requireExisting) {
  if (cartAttrsStamped) return;

  // Without consent, the Elevate app embed's own session() returns
  // userContext: 'ANONYMOUS' - no customerKey/sessionKey at all - so there is
  // nothing here worth attributing an order to. Skip the write entirely
  // rather than stamp a value that does not exist.
  //
  // Awaited first for the same reason as syncDiscoveryKeyWithEngage above -
  // hasAnalyticsConsent() alone races the Customer Privacy API's own async
  // load this early in the page lifecycle.
  await window.CGElevate.loadCustomerPrivacy();
  if (!window.CGElevate.hasAnalyticsConsent()) return;

  const elevate = await whenElevateReady();
  if (!elevate) return;

  // session() is asynchronous - the SDK's localStorage-backed session reads
  // are not synchronous despite how call sites elsewhere in this theme treat
  // the URL-building path internally. Confirmed empirically: reading it
  // without awaiting returns a Promise object, whose .customerKey/.sessionKey
  // are always undefined, silently defeating this whole function.
  const session = (await elevate.query.c.session()) || {};
  const market = elevate.query.c.market;
  const customerKey = customerKeyOverride || session.customerKey;
  const sessionKey = session.sessionKey;

  // Without all three the attribution is worthless, so we write nothing.
  if (!customerKey || !sessionKey || !market) return;

  const cartRes = await fetch('/cart.js');
  if (!cartRes.ok) return;

  const cart = await cartRes.json();

  if (requireExisting && !cart.item_count) return;

  const attributes = cart.attributes || {};
  const alreadyCorrect =
    attributes._elevate_customer_key === customerKey &&
    attributes._elevate_session_key === sessionKey &&
    attributes._elevate_market === market;

  if (alreadyCorrect) {
    cartAttrsStamped = true;
    return;
  }

  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        _elevate_customer_key: customerKey,
        _elevate_session_key: sessionKey,
        _elevate_market: market
      },
      items: []
    })
  });

  cartAttrsStamped = true;
}

// The subscriber is registered immediately, not inside the readiness check.
// Otherwise the first add-to-cart is lost when the Elevate app loads late.
// No source filtering here, unlike assets/cart.js:78-85: both 'product-form'
// and 'cart-items' must trigger a write.
subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
  // requireExisting = false: here we know something was actually added.
  writeElevateCartAttributes(null, false).catch(() => {});
});

// Covers: visitor adds to cart before deciding on cookies, then accepts - the
// Elevate app embed's own consent.js listens for the same event and stamps
// the cart itself (its cartSync(), same attribute keys), confirmed live
// 2026-09-22 by blocking this theme's own write and granting consent through
// Shopify's real Customer Privacy API: the cart was stamped anyway. Removed
// as a duplicate rather than kept as a redundant safety net.

/**
 * The ticket to send with the add-to-cart notification, captured
 * synchronously on submit rather than read out of the cartUpdate payload:
 * PUB_SUB_EVENTS carries only `{source, productVariantId}`, nothing that
 * identifies which card or page the add came from - by the time cartUpdate
 * fires, the form that was submitted is no longer available.
 *
 * Two sources, and they must not be confused:
 *
 * - Quick-add, from a product card (snippets/product-card.liquid's own
 *   `.card-wrapper`) still on screen: only that card's own ticket is trusted,
 *   never lastClickedTicket() - a card renders from more than one source.
 *   assets/elevate-native-cards.js / assets/elevate-card.js set the ticket on
 *   cards Elevate actually rendered (a PLP grid, an Elevate recommendation
 *   carousel); a card from sections/product-recommendations.liquid (Shopify's
 *   own "customers also bought", still present on the product page) or a
 *   leftover Klevu block carries none. Falling back to the last click there
 *   would misattribute the add to whichever different product the visitor
 *   clicked through Elevate to even reach this page - confirmed empirically:
 *   product-recommendations.liquid renders real quick-add
 *   `<product-form>`s with no [data-voyado-ticket] ancestor at all.
 * - The main product page's own buy box, which is never a `.card-wrapper` and
 *   carries no ticket of its own: falls back to whichever product the
 *   visitor most recently clicked through Elevate
 *   (assets/elevate-core.js's lastClickedTicket()) - last-touch attribution,
 *   the closest available approximation of "how did they get here". Correct
 *   only because this specific form is for the page's own product.
 *
 * A product added without either - a bookmark, a direct PDP visit with no
 * prior Elevate click this session - has no ticket at all, and is silently
 * skipped rather than sent with one that would misattribute it.
 */
let pendingAddToCartTicket = '';

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest('product-form');
    if (!form) return;
    const card = form.closest('.card-wrapper');
    pendingAddToCartTicket = card ? card.dataset.voyadoTicket || '' : window.CGElevate.lastClickedTicket();
  },
  true
);

subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
  if (!event || event.source !== 'product-form') return;
  const ticket = pendingAddToCartTicket;
  pendingAddToCartTicket = '';
  if (!ticket) return;
  whenElevateReady().then((elevate) => {
    if (elevate) window.CGElevate.notifyAddToCart(ticket);
  });
});

/**
 * Job 5: favorite notifications, from the Wishlist Guru app's heart button.
 *
 * Elevate's FAVORITES algorithm has no data source without these, so a
 * merchandiser can configure the list and still get nothing back.
 *
 * The heart is rendered by Wishlist Guru (a Shopify app embed), not by this
 * theme, so this is a bridge rather than a handler we own. Two things make
 * that bridge possible:
 *
 * - The app puts the Shopify product id on its own wrapper as `product-id`,
 *   and that is exactly Elevate's productKey - verified against a live
 *   Elevate response, same value on both sides. No lookup needed.
 * - The app calls `fxnAfterAddToWishlist`/`fxnAfterRemoveFromWishlist`
 *   (globals it defines for merchant custom JS) once the wishlist write has
 *   actually gone through. They take no arguments, hence the click listener
 *   below to capture *which* product - but they are the only signal that
 *   distinguishes an add from a remove, and the only one that fires on
 *   success rather than on intent. A click the app aborts (its own EU
 *   privacy check, a failed request) never reaches them, so nothing is sent.
 *
 * Wrapping happens on click rather than at load: the app's script is an
 * async app embed with no readiness event, but a click on its own button
 * proves it has loaded. Avoids polling or racing its definition. See
 * wrapWishlistHook for what happens when a hook is still missing.
 *
 * Three surfaces remove a favorite, and they do not behave alike:
 *
 * - The heart itself, on a product card or product page: hook fires.
 * - The bin on the wishlist page: hook fires too, so it rides the same path,
 *   it just needs its own selector since the bin is not a heart.
 * - "Clear all" on the wishlist page: fires NO hook at all, confirmed
 *   empirically. Handled separately in notifyClearedWishlist below.
 *
 * Quantity on the wishlist page is deliberately ignored. Elevate's
 * add-favorite carries a productKey and nothing else - a favorite is binary,
 * in the list or not - so sending it again on an increment would double-count
 * the same signal. The app agrees: it fires no hook for a quantity change.
 */
const WISHLIST_HEART_SELECTOR = '.wf-wishlist-collection-icon[product-id], .wf-wishlist[product-id]';
const WISHLIST_REMOVE_SELECTOR = '[onclick*="removeItem"]';
const WISHLIST_CLEAR_SELECTOR = '[onclick*="clearAllWishlist"]';

// Scoped to the wishlist's own items on purpose: the wishlist page also
// renders recommendation cards, whose hearts carry product-id too. Matching
// those would remove favorites the visitor never had - 16 ids on the page
// against 2 real entries when this was checked.
const WISHLIST_PAGE_ITEM_SELECTOR = '.wishlist-modal-all .product-content-sec[product-id]';

const WISHLIST_CLEAR_POLL_MS = 150;
const WISHLIST_CLEAR_TIMEOUT_MS = 4000;

let pendingFavoriteKeys = [];

/**
 * The product a wishlist-page control belongs to, read off the nearest
 * ancestor that holds exactly one. Refuses to guess when an ancestor holds
 * several, rather than picking the first and removing the wrong product.
 */
function productKeyNear(element) {
  let node = element.parentElement;
  for (let i = 0; i < 3 && node; i += 1) {
    const keys = [...new Set([...node.querySelectorAll('[product-id]')].map((el) => el.getAttribute('product-id')).filter(Boolean))];
    if (keys.length === 1) return keys[0];
    if (keys.length > 1) return '';
    node = node.parentElement;
  }
  return '';
}

function wishlistPageProductKeys() {
  const keys = [...document.querySelectorAll(WISHLIST_PAGE_ITEM_SELECTOR)].map((el) => el.getAttribute('product-id')).filter(Boolean);
  // The page renders each entry twice (one layout per breakpoint), so the
  // same id comes back more than once.
  return [...new Set(keys)];
}

/**
 * "Clear all" gets no hook, so success has to be observed instead: wait for
 * the list to actually empty before notifying. Keeps the same rule the hooks
 * give us everywhere else - notify on what happened, not on what was clicked
 * - so a clear that is cancelled or fails leaves Elevate untouched.
 */
function notifyClearedWishlist(keys) {
  if (!keys.length) return;
  const deadline = Date.now() + WISHLIST_CLEAR_TIMEOUT_MS;
  const check = () => {
    if (!wishlistPageProductKeys().length) {
      whenElevateReady().then((elevate) => {
        if (elevate) keys.forEach((key) => window.CGElevate.notifyRemoveFavorite(key));
      });
      return;
    }
    if (Date.now() < deadline) setTimeout(check, WISHLIST_CLEAR_POLL_MS);
  };
  setTimeout(check, WISHLIST_CLEAR_POLL_MS);
}

/**
 * Re-checked on every click, not once: the hooks are not always defined by
 * the time of the first click (seen on the live theme after the switch, with
 * both globals undefined). The app only calls them if they exist, so when
 * one is missing the theme defines it itself, and when the app (or custom JS
 * in its settings) defines or replaces it later, the next click wraps that
 * one instead. The marker keeps a function from being wrapped twice, which
 * would send every favorite twice.
 */
const WRAPPED_MARKER = '__cgElevateFavoriteHook';

function wrapWishlistHook(name, notify) {
  const current = window[name];
  if (typeof current === 'function' && current[WRAPPED_MARKER]) return;
  const original = typeof current === 'function' ? current : null;
  const wrapped = function (...args) {
    const keys = pendingFavoriteKeys;
    pendingFavoriteKeys = [];
    if (keys.length) {
      whenElevateReady().then((elevate) => {
        if (elevate) keys.forEach((key) => notify(key));
      });
    }
    return original ? original.apply(this, args) : undefined;
  };
  wrapped[WRAPPED_MARKER] = true;
  window[name] = wrapped;
}

function wrapWishlistHooks() {
  wrapWishlistHook('fxnAfterAddToWishlist', window.CGElevate.notifyAddFavorite);
  wrapWishlistHook('fxnAfterRemoveFromWishlist', window.CGElevate.notifyRemoveFavorite);
}

document.addEventListener(
  'click',
  (event) => {
    if (!event.target.closest) return;

    const heart = event.target.closest(WISHLIST_HEART_SELECTOR);
    if (heart) {
      pendingFavoriteKeys = [heart.getAttribute('product-id')].filter(Boolean);
      wrapWishlistHooks();
      return;
    }

    const removeButton = event.target.closest(WISHLIST_REMOVE_SELECTOR);
    if (removeButton) {
      pendingFavoriteKeys = [productKeyNear(removeButton)].filter(Boolean);
      wrapWishlistHooks();
      return;
    }

    if (event.target.closest(WISHLIST_CLEAR_SELECTOR)) {
      notifyClearedWishlist(wishlistPageProductKeys());
    }
  },
  true
);

whenElevateReady().then(async (elevate) => {
  if (!elevate) return;

  // A newsletter link takes priority when both are present - it carries an
  // actual identity from Engage, worth adopting before considering whatever
  // this browser already had.
  const linkedCustomerKey = (await identifyFromEclubLink(elevate).catch(() => null)) || (await syncDiscoveryKeyWithEngage(elevate).catch(() => null));

  // Covers the customer returning to a saved cart: a new session means a new
  // sessionKey, so the values on the cart need updating. requireExisting = true
  // so we do not create carts for visitors who have not shopped.
  writeElevateCartAttributes(linkedCustomerKey, true).catch(() => {});
});
